import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
function DesktopMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden md:flex items-center gap-6 animate-pulse">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-4 w-20 bg-muted/20 rounded" />)}
      </div>;
  }
  return <NavigationMenu className="hidden md:flex">
      <NavigationMenuList className="gap-8">
        {menuItems.map(item => <NavigationMenuItem key={item.id}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className={cn("relative text-sm font-semibold uppercase tracking-wider text-foreground transition-colors hover:text-primary", "after:absolute after:bottom-[-4px] after:left-0 after:h-[2px] after:w-full after:origin-left after:scale-x-0 after:bg-primary after:transition-transform after:duration-300 hover:after:scale-x-100", "font-sans")}>
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenu({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-40 flex flex-col bg-background/95 backdrop-blur-xl pt-24 px-6 md:hidden animate-in fade-in slide-in-from-top-5 duration-200">
      <nav className="flex flex-col gap-6">
        {loading ? <div className="flex flex-col gap-4 animate-pulse">
            {[1, 2, 3, 4].map(i => <div key={i} className="h-8 w-32 bg-muted/20 rounded" />)}
          </div> : menuItems.map(item => <Link key={item.id} to={item.href || "#"} className="text-2xl font-bold text-foreground hover:text-primary transition-colors font-sans" onClick={onClose}>
              {item.label}
            </Link>)}
        <div className="mt-8">
          <Button asChild className="w-full rounded-none bg-primary text-primary-foreground hover:bg-primary/90 text-lg py-6 font-bold uppercase tracking-wide">
            <Link to="tel:317-709-9540" onClick={onClose}>
              call to schedule a quote
            </Link>
          </Button>
        </div>
      </nav>
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className={cn("fixed top-0 left-0 right-0 z-50 w-full transition-all duration-300 border-b border-transparent", isScrolled ? "bg-background/80 backdrop-blur-lg border-white/10 py-3 shadow-sm" : "bg-background py-5")}>
      <MenuProvider menu_id="16" className="w-full">
        <div className="container mx-auto px-4 md:px-6 flex items-center justify-between">
          {}
          <Link to="/HomePage#" className="flex-shrink-0 z-50">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1568171/img/image_7hzhto0j.png" alt="ConcreteCraft" className="h-7 w-auto object-contain object-bottom logoo-img" />
          </Link>

          {}
          <DesktopMenu />

          {}
          <div className="flex items-center gap-4 z-50">
            <Button asChild className="hidden md:inline-flex rounded-none bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase tracking-wide px-6">
              <Link to="tel:317-709-9540">call to schedule a quote</Link>
            </Button>

            {}
            <Button variant="ghost" size="icon" className="md:hidden text-foreground hover:text-primary hover:bg-transparent" onClick={() => setIsMobileOpen(!isMobileOpen)} aria-label="Toggle menu">
              {isMobileOpen ? <X className="size-8" /> : <Menu className="size-8" />}
            </Button>
          </div>
        </div>

        {}
        <MobileMenu isOpen={isMobileOpen} onClose={() => setIsMobileOpen(false)} />
      </MenuProvider>
    </header>;
}